import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { StatusIndicator } from '@/modules/wms/components/common/StatusIndicator';
import { WMSStatCard } from '@/modules/wms/components/dashboard/WMSStatCard';
import { ScanInput } from '@/modules/wms/components/common/ScanInput';
import { adjustmentsAPI, wmsAPI } from '@/shared/services/api';
import {
  Search,
  Plus,
  FileText,
  ClipboardCheck,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ArrowUpDown,
  MoreHorizontal,
  Download,
  Upload,
  RefreshCw,
  Layers,
  TrendingDown,
  TrendingUp,
  Loader2 } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';
import { toast } from 'sonner';

export const InventoryAdjustments = () => {const { t } = useTranslation("wms");
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(false);
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjustments, setAdjustments] = useState([]);
  const [cycleCounts, setCycleCounts] = useState([]);
  const [adjustmentReasons, setAdjustmentReasons] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, totalVariance: 0, cycleCountsActive: 0 });
  const [adjustmentForm, setAdjustmentForm] = useState({
    sku: '',
    location: '',
    reason: '',
    quantity: '',
    notes: ''
  });

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [adjustmentsData, cycleCountsData, reasonsData, statsData, inventoryData] = await Promise.all([
        adjustmentsAPI.getAdjustments(),
        adjustmentsAPI.getCycleCounts(),
        adjustmentsAPI.getReasons(),
        adjustmentsAPI.getStats(),
        wmsAPI.getInventory()]
        );
        setAdjustments(adjustmentsData || []);
        setCycleCounts(cycleCountsData || []);
        setAdjustmentReasons(reasonsData || []);
        setInventory(inventoryData || []);
        setStats({
          pending: statsData?.pending || 0,
          approved: statsData?.approved || 0,
          totalVariance: statsData?.total_variance || 0,
          cycleCountsActive: statsData?.active_cycle_counts || 0
        });
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load adjustments data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleScan = (barcode) => {
    const item = inventory.find((i) => i.sku === barcode);
    if (item) {
      setAdjustmentForm((prev) => ({
        ...prev,
        sku: item.sku,
        location: item.location || item.bin_location || ''
      }));
      setShowAdjustmentDialog(true);
    } else {
      toast.error('Item not found');
    }
  };

  const handleBatchSelect = (id) => {
    setSelectedItems((prev) =>
    prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleCreateAdjustment = async () => {
    try {
      await adjustmentsAPI.createAdjustment({
        sku: adjustmentForm.sku,
        location: adjustmentForm.location,
        reason: adjustmentForm.reason,
        new_quantity: parseInt(adjustmentForm.quantity),
        notes: adjustmentForm.notes
      });
      toast.success('Adjustment created successfully');
      setShowAdjustmentDialog(false);
      // Refresh data
      const [adjustmentsData, statsData] = await Promise.all([
      adjustmentsAPI.getAdjustments(),
      adjustmentsAPI.getStats()]
      );
      setAdjustments(adjustmentsData || []);
      setStats({
        pending: statsData?.pending || 0,
        approved: statsData?.approved || 0,
        totalVariance: statsData?.total_variance || 0,
        cycleCountsActive: statsData?.active_cycle_counts || 0
      });
      setAdjustmentForm({ sku: '', location: '', reason: '', quantity: '', notes: '' });
    } catch (error) {
      console.error('Failed to create adjustment:', error);
      toast.error('Failed to create adjustment');
    }
  };

  const handleBatchApprove = async () => {
    try {
      await adjustmentsAPI.batchApprove(selectedItems);
      toast.success(`Approved ${selectedItems.length} adjustments`);
      setShowBatchDialog(false);
      setSelectedItems([]);
      // Refresh
      const adjustmentsData = await adjustmentsAPI.getAdjustments();
      setAdjustments(adjustmentsData || []);
    } catch (error) {
      console.error('Failed to batch approve:', error);
      toast.error('Failed to batch approve');
    }
  };

  return (
    <div className="space-y-6" data-testid="wms-inventory-adjustments">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("inventoryAdjustments", "Inventory Adjustments")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manualStockCorrections", "Manual stock corrections, cycle counting, and audit logs")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("exportLog", "Export Log")}

          </Button>
          <Button
            className="gap-2 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]"
            onClick={() => setShowAdjustmentDialog(true)}>
            
            <Plus className="h-4 w-4" />{t("newAdjustment", "New Adjustment")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <WMSStatCard
          title="Pending Approvals"
          value={stats.pending}
          icon={Clock}
          color="yellow"
          subtitle="Awaiting review" />
        
        <WMSStatCard
          title="Approved Today"
          value={stats.approved}
          icon={CheckCircle2}
          color="green"
          subtitle="Adjustments processed" />
        
        <WMSStatCard
          title="Net Variance"
          value={stats.totalVariance}
          icon={stats.totalVariance < 0 ? TrendingDown : TrendingUp}
          color={stats.totalVariance < 0 ? 'red' : 'blue'}
          subtitle="Units adjusted" />
        
        <WMSStatCard
          title="Active Cycle Counts"
          value={stats.cycleCountsActive}
          icon={RefreshCw}
          color="purple"
          subtitle="In progress" />
        
      </div>

      {/* Scan Input */}
      <Card className="card-elevated bg-gradient-to-r from-indigo-50/50 to-transparent dark:from-indigo-950/20">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/50">
              <ClipboardCheck className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold">{t("quickAdjustment", "Quick Adjustment")}</h3>
              <p className="text-sm text-muted-foreground">{t("scanItemBarcode", "Scan item barcode to create an adjustment")}</p>
            </div>
          </div>
          <ScanInput
            placeholder="Scan item barcode or enter SKU..."
            onScan={handleScan} />
          
        </CardContent>
      </Card>

      {/* Batch Actions */}
      {selectedItems.length > 0 &&
      <Card className="card-elevated border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5 text-blue-600" />
              <span className="font-medium">{selectedItems.length}{t("itemsSelected", "items selected")}</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setSelectedItems([])}>{t("clearSelection", "Clear Selection")}

            </Button>
              <Button size="sm" onClick={() => setShowBatchDialog(true)}>{t("batchApprove", "Batch Approve")}

            </Button>
            </div>
          </CardContent>
        </Card>
      }

      {/* Tabs */}
      <Tabs defaultValue="adjustments" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="adjustments" className="gap-2">
            <FileText className="h-4 w-4" />{t("adjustments", "Adjustments")}

            <Badge variant="secondary" className="ml-1">{adjustments.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="cycle-counts" className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("cycleCounts", "Cycle Counts")}

            <Badge variant="secondary" className="ml-1">{cycleCounts.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="audit-log">{t("auditLog", "Audit Log")}</TabsTrigger>
        </TabsList>

        <TabsContent value="adjustments" className="space-y-4">
          <Card className="card-elevated">
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle className="text-lg">{t("adjustmentRecords", "Adjustment Records")}</CardTitle>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by SKU or product..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10" />
                  
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="w-12 py-3 px-4">
                        <Checkbox
                          checked={selectedItems.length === adjustments.length && adjustments.length > 0}
                          onCheckedChange={(checked) => {
                            setSelectedItems(checked ? adjustments.map((a) => a.id) : []);
                          }} />
                        
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("id", "ID")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("product", "Product")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("location", "Location")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("previous", "Previous")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("new", "New")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("variance", "Variance")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("reason", "Reason")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ?
                    <tr>
                        <td colSpan={10} className="py-8 text-center">
                          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                          <p className="text-sm text-muted-foreground mt-2">{t("loadingAdjustments", "Loading adjustments...")}</p>
                        </td>
                      </tr> :
                    adjustments.length === 0 ?
                    <tr>
                        <td colSpan={10} className="py-8 text-center">
                          <FileText className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                          <p className="text-sm text-muted-foreground">{t("noAdjustmentsFound", "No adjustments found")}</p>
                        </td>
                      </tr> :
                    adjustments.map((adjustment) =>
                    <tr key={adjustment.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                        <td className="py-4 px-4">
                          <Checkbox
                          checked={selectedItems.includes(adjustment.id)}
                          onCheckedChange={() => handleBatchSelect(adjustment.id)} />
                        
                        </td>
                        <td className="py-4 px-4">
                          <span className="font-mono text-sm font-medium">{adjustment.id}</span>
                        </td>
                        <td className="py-4 px-4">
                          <div>
                            <span className="font-mono text-sm">{adjustment.sku}</span>
                            <p className="text-xs text-muted-foreground">{adjustment.productName}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="font-mono text-sm">{adjustment.location}</span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span>{adjustment.previousQty}</span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="font-medium">{adjustment.newQty}</span>
                        </td>
                        <td className="py-4 px-4">
                          <div className={`flex items-center gap-1 ${
                        adjustment.variance > 0 ? 'text-green-600' : 'text-red-600'}`
                        }>
                            {adjustment.variance > 0 ?
                          <TrendingUp className="h-4 w-4" /> :

                          <TrendingDown className="h-4 w-4" />
                          }
                            <span className="font-medium">{adjustment.variance > 0 ? '+' : ''}{adjustment.variance}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm capitalize">{(adjustment.reason || '').replace('_', ' ')}</span>
                        </td>
                        <td className="py-4 px-4">
                          <StatusIndicator status={adjustment.status} />
                        </td>
                        <td className="py-4 px-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>{t("viewDetails", "View Details")}</DropdownMenuItem>
                              {adjustment.status === 'pending' &&
                            <>
                                  <DropdownMenuItem className="text-green-600">{t("approve", "Approve")}</DropdownMenuItem>
                                  <DropdownMenuItem className="text-red-600">{t("reject", "Reject")}</DropdownMenuItem>
                                </>
                            }
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cycle-counts" className="space-y-4">
          <Card className="card-elevated">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">{t("cycleCountSchedule", "Cycle Count Schedule")}</CardTitle>
              <Button size="sm" className="gap-2">
                <Plus className="h-4 w-4" />{t("scheduleCount", "Schedule Count")}

              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("id", "ID")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("zone", "Zone")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("scheduledDate", "Scheduled Date")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("progress", "Progress")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("variance", "Variance")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("assignedTo", "Assigned To")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cycleCounts.length === 0 ?
                    <tr>
                        <td colSpan={8} className="py-8 text-center">
                          <RefreshCw className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                          <p className="text-sm text-muted-foreground">{t("noCycleCounts", "No cycle counts scheduled")}</p>
                        </td>
                      </tr> :
                    cycleCounts.map((count) =>
                    <tr key={count.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                        <td className="py-4 px-4">
                          <span className="font-mono text-sm font-medium">{count.id}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm">{count.zone}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm">{count.scheduledDate}</span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span>{count.itemsCounted} / {count.itemsToCount}</span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          {count.variance !== null ?
                        <span className={count.variance !== 0 ? 'text-amber-600 font-medium' : 'text-green-600'}>
                              {count.variance !== 0 ? count.variance : 'No variance'}
                            </span> :
                        '-'}
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm">{count.assignedTo}</span>
                        </td>
                        <td className="py-4 px-4">
                          <StatusIndicator status={count.status === 'in_progress' ? 'picking' : count.status === 'completed' ? 'in_stock' : 'pending'} />
                        </td>
                        <td className="py-4 px-4 text-right">
                          <Button
                          size="sm"
                          variant={count.status === 'completed' ? 'outline' : 'default'}>
                          
                            {count.status === 'scheduled' && 'Start Count'}
                            {count.status === 'in_progress' && 'Continue'}
                            {count.status === 'completed' && 'View Report'}
                          </Button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit-log">
          <Card className="card-elevated">
            <CardContent className="p-8 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-semibold mb-2">{t("auditLog", "Audit Log")}</h3>
              <p className="text-sm text-muted-foreground mb-4">{t("completeHistoryOf", "Complete history of all inventory changes and adjustments")}

              </p>
              <Button variant="outline">{t("loadAuditHistory", "Load Audit History")}</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Adjustment Dialog */}
      <Dialog open={showAdjustmentDialog} onOpenChange={setShowAdjustmentDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("createInventoryAdjustment", "Create Inventory Adjustment")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium mb-2 block">{t("sku", "SKU *")}</label>
                <Input
                  placeholder="Enter SKU"
                  value={adjustmentForm.sku}
                  onChange={(e) => setAdjustmentForm((prev) => ({ ...prev, sku: e.target.value }))} />
                
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">{t("location", "Location")}</label>
                <Input
                  placeholder="e.g., A-01-001"
                  value={adjustmentForm.location}
                  onChange={(e) => setAdjustmentForm((prev) => ({ ...prev, location: e.target.value }))} />
                
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t("adjustmentReason", "Adjustment Reason *")}</label>
              <select
                className="w-full h-10 px-3 border border-input rounded-md bg-background"
                value={adjustmentForm.reason}
                onChange={(e) => setAdjustmentForm((prev) => ({ ...prev, reason: e.target.value }))}>
                
                <option value="">{t("selectReason", "Select reason")}</option>
                {adjustmentReasons.map((r) =>
                <option key={r.id} value={r.id}>{r.label}</option>
                )}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t("newQuantity", "New Quantity *")}</label>
              <Input
                type="number"
                placeholder="Enter new quantity"
                value={adjustmentForm.quantity}
                onChange={(e) => setAdjustmentForm((prev) => ({ ...prev, quantity: e.target.value }))} />
              
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t("notes", "Notes")}</label>
              <Input
                placeholder="Add notes for this adjustment"
                value={adjustmentForm.notes}
                onChange={(e) => setAdjustmentForm((prev) => ({ ...prev, notes: e.target.value }))} />
              
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAdjustmentDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button onClick={() => setShowAdjustmentDialog(false)}>{t("submitAdjustment", "Submit Adjustment")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Approve Dialog */}
      <Dialog open={showBatchDialog} onOpenChange={setShowBatchDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("batchApproveAdjustments", "Batch Approve Adjustments")}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">{t("youAreAbout", "You are about to approve")}
              <span className="font-semibold">{selectedItems.length}</span>{t("adjustmentsThisAction", "adjustment(s).\n              This action cannot be undone.")}

            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowBatchDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button onClick={() => {setShowBatchDialog(false);setSelectedItems([]);}}>{t("approveAll", "Approve All")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};